import Immutable from 'immutable';
import { compact, min, max } from 'lodash';
import moment from 'moment';
import ResourceTree from 'components/timeline_control/resources/resource_tree';
import { DateRange } from '../records';

const dateArray = (resourceTree, dateType) => {
  const utcDates = resourceTree.rows.map(resourceDate => {
    const date = resourceDate ? resourceDate[dateType] : null;
    return date ? moment.utc(date) : null;
  });

  return compact(utcDates) || [];
};

const dateWithFallback = date => {
  if (date === Infinity || date === -Infinity || !date) {
    return moment.utc();
  } else {
    return date;
  }
};

const getDateRange = (payload, resourceTree) => {
  const { timeScale, zoom } = payload.dates;

  const milestoneDates = payload.milestones.map(milestone => {
    return moment.utc(milestone.date);
  });

  const fromDates = dateArray(resourceTree, 'from').concat(milestoneDates);
  const toDates = dateArray(resourceTree, 'to').concat(milestoneDates);

  const minFromDate = dateWithFallback(min(fromDates));
  const automaticMinDate = minFromDate.subtract(7, 'days');

  let minDate =
    zoom === 'fit' ? automaticMinDate : utcDate(payload.dates.minDate);
  if (!minDate) {
    minDate = automaticMinDate;
  }

  const maxToDate = calculateMaxToDate(payload, toDates, fromDates);
  const automaticMaxDate = maxToDate.add(7, 'days');

  let maxDate =
    zoom === 'fit' ? automaticMaxDate : utcDate(payload.dates.maxDate);
  if (!maxDate) {
    maxDate = automaticMaxDate;
  }

  const { startDate, endDate } = validateDates(payload, minDate, maxDate);

  const dateRange = new DateRange({
    automaticMaxDate,
    automaticMinDate,
    maxToDate,
    minFromDate,
    maxDate,
    minDate,
    toDate: getToDate(endDate, maxDate, automaticMaxDate, zoom),
    fromDate: getFromDate(startDate, minDate, automaticMinDate, zoom),
    timeScale,
    zoom,
  });

  return dateRange;
};

const calculateMaxToDate = (payload, toDates, fromDates) => {
  return dateWithFallback(max(fromDates.concat(toDates)));
};

const validateDates = (payload, minDate, maxDate) => {
  let { startDate, endDate } = payload;

  if (startDate && moment.utc(startDate).isBefore(minDate)) {
    startDate = null;
  }

  if (endDate && moment.utc(endDate).isAfter(maxDate)) {
    endDate = null;
  }

  if (
    startDate &&
    endDate &&
    moment.utc(startDate).isAfter(moment.utc(endDate))
  ) {
    startDate = null;
    endDate = null;
  }

  return { startDate, endDate };
};

const utcDate = date => {
  if (date) {
    return moment.utc(date);
  }
};

const getToDate = (endDate, maxDate, automaticMaxDate, zoom) => {
  if (zoom === 'fit') {
    return automaticMaxDate.clone();
  } else {
    return endDate || (maxDate || automaticMaxDate).clone();
  }
};

const getFromDate = (startDate, minDate, automaticMinDate, zoom) => {
  if (zoom === 'fit') {
    return automaticMinDate.clone();
  } else {
    return startDate || (minDate || automaticMinDate).clone();
  }
};

export default (state = Immutable.Map(), action) => {
  switch (action.type) {
    case 'HYDRATE_TIMELINE_CONFIG': {
      const { data, timelineType } = action.payload;
      const resourceTree = new ResourceTree(data, timelineType);
      return getDateRange(action.payload, resourceTree);
    }

    case 'SET_DATES':
      return state.merge(action.payload.dates);

    case 'RESET_DATES':
      return state
        .set('fromDate', state.get('minDate'))
        .set('toDate', state.get('maxDate'));

    case 'CHANGE_TIME_SCALE':
      return state.set('timeScale', action.payload);

    case 'CHANGE_ZOOM':
      return state.set('zoom', action.payload);

    case 'CHANGE_MIN_DATE':
      return state.set('minDate', action.payload);

    case 'CHANGE_MAX_DATE':
      return state.set('maxDate', action.payload);

    default:
      return state;
  }
};
